import React, { useContext, useEffect, useState } from 'react';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridCellParams, GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import Confirm from '../../../components/confirm';
import CustomDataGrid from '../../../components/data-grid-base';
import SaveBackdrop from '../../../components/saveBackdrop';
import Screen from '../../../components/screen';
import SplashWait from '../../../components/splash-wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadEnrollmentFile, canWriteEnrollmentFile,
  checkUserAuthenticated, formatDateForDataGrid, getBreadcrumbMaxItems,
  getPreEnrollBatchPendingFileItems, goToRoutePage, setWindowTitle
} from '../../../utils/helper';
import {
  DeleteIcon, EnrollIcon, GetAppToneIcon, MoreIcon, RefreshIcon
} from '../../../utils/icons';
import {
  Badge, Box, Breadcrumbs, Button, ButtonGroup, Checkbox, FormControlLabel, FormGroup, Grid,
  Hidden, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography
} from '../../../utils/material-ui-core';

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const EnrollmentFileReview = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { writeLog, showAlert, userFunctionAccess, isUserAuthenticated, enrollmentBatches, refreshData } = useContext(AppContext);

  const { preBatchLoadKey } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);
  const [enrollmentBatchFileItems, setEnrollmentBatchFileItems] = React.useState<PreEnrollBatchPendingFileItem[]>([]);
  const [filterEnrollmentBatchFileItems, setFilterEnrollmentBatchFileItems] = React.useState<PreEnrollBatchPendingFileItem[]>([]);
  const [filterRowType, setFilterRowType] = useState<number>(1);
  const [enrollRows, setEnrollRows] = useState<number>(0);
  const [issueRows, setIssueRows] = useState<number>(0);
  const [enrollAll, setEnrollAll] = useState<boolean>(false);
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [isDatagridInEditMode, setIsDatagridInEditMode] = useState<boolean>(false);
  const [fileId, setFileId] = useState<number>(0);
  const [listTitle, setListTitle] = useState("");
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [enrollConfirmDialogOpen, setEnrollConfirmDialogOpen] = useState<boolean>(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);

  const columns = [
    { field: "readyToEnroll", headerName: "Enroll", width: 200, hide: false, type: "boolean", editable: true, headerClassName: "editColumn" },

    { field: "errorMessage", headerName: "Error", width: 120, hide: false, headerClassName: "editColumn", disableExport: true },

    { field: "dataValidationMessage", headerName: "Data Validation Message", width: 350, hide: true },

    { field: "companyCode", headerName: "Company Code", width: 250, hide: true },
    { field: "programCode", headerName: "Program Code", width: 250, hide: true },
    { field: "parentAccount", headerName: "Parent Account", width: 250, hide: true },
    { field: "signupKey", headerName: "Signup Key", width: 250, hide: true },

    { field: "policyType", headerName: "Policy Type", width: 250, hide: true },
    { field: "policyNumber", headerName: "Policy#", width: 250, hide: true },
    { field: "polEffDate", headerName: "Effective Date", width: 250, type: "date", hide: true, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "polExpDate", headerName: "Expiry Date", width: 250, type: "date", hide: true, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "premise", headerName: "Premise", width: 250, hide: true },
    { field: "group", headerName: "Group", width: 250, hide: true },
    { field: "buildingType", headerName: "Building Type", width: 250, hide: true },
    { field: "occupancyCode", headerName: "Occupancy Code", width: 250, hide: true },
    { field: "bcovTiv", headerName: "BcovTiv", width: 250, hide: true, type: "number" },

    { field: "accountNumber", headerName: "Account Number", width: 250, hide: false },
    { field: "accountName", headerName: "Account Name", width: 250, hide: false },
    { field: "accountAddressline1", headerName: "Address Line 1", width: 250, hide: true },
    { field: "accountAddressline2", headerName: "Address Line 2", width: 250, hide: true },
    { field: "accountCity", headerName: "City", width: 250, hide: true },
    { field: "accountState", headerName: "State", width: 250, hide: true },
    { field: "accountPostalCode", headerName: "Postal Code", width: 250, hide: true },
    { field: "accountCountry", headerName: "Country", width: 250, hide: true },
    { field: "accountAddressValidationMessage", headerName: "Account Address Validation Message", width: 350, hide: false },

    { field: "kitNo", headerName: "Kit No", width: 250, hide: true, type: "number" },

    { field: "sidewalkValidationMessage", headerName: "Sidewalk Validation Message", width: 350, hide: false },
    { field: "installOption", headerName: "Install Option", width: 250, hide: true },

    { field: "contactFirstName", headerName: "Contact First Name", width: 250, hide: true },
    { field: "contactLastName", headerName: "Contact Last Name", width: 250, hide: true },
    { field: "contactEmail", headerName: "Contact Email", width: 250, hide: true },
    { field: "contactEmailValidationMessage", headerName: "Contact Email Validation Message", width: 350, hide: false },
    { field: "contactPhone", headerName: "Contact Phone", width: 250, hide: true },
    { field: "contactPhoneType", headerName: "Contact Phone Type", width: 350, hide: false },

    { field: "shippingOrganizationName", headerName: "Organization Name", width: 250, hide: true },
    { field: "shippingFirstName", headerName: "First Name", width: 250, hide: true },
    { field: "shippingLastName", headerName: "Last Name", width: 250, hide: true },
    { field: "shippingEmail", headerName: "Email", width: 250, hide: true },
    { field: "shippingEmailValidationMessage", headerName: "Shipping Email Validation Message", width: 350, hide: false },
    { field: "shippingPhone", headerName: "Phone", width: 250, hide: true },
    { field: "shippingPhoneType", headerName: "Shipping Phone Type", width: 350, hide: false },

    { field: "shippingAddressline1", headerName: "Address Line 1", width: 250, hide: true },
    { field: "shippingAddressline2", headerName: "Address Line 2", width: 250, hide: true },
    { field: "shippingCity", headerName: "City", width: 250, hide: true },
    { field: "shippingState", headerName: "State", width: 250, hide: true },
    { field: "shippingPostalCode", headerName: "Postal Code", width: 250, hide: true },
    { field: "shippingCountry", headerName: "Country", width: 250, hide: true },
    { field: "shippingAddressValidationMessage", headerName: "Shipping Address Validation Message", width: 350, hide: false },

    { field: "participantAgreementDate", headerName: "Participant Agreement Date", width: 250, type: "date", hide: true, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "participantAgreementSignedByFirstName", headerName: "Signed By First Name", width: 250, hide: true },
    { field: "participantAgreementSignedByLastName", headerName: "Signed By Last Name", width: 250, hide: true },

    { field: "userDefinedField1", headerName: "User Defined Field 1", width: 250, hide: true },
    { field: "userDefinedField2", headerName: "User Defined Field 2", width: 250, hide: true },
    { field: "userDefinedField3", headerName: "User Defined Field 3", width: 250, hide: true },
    { field: "userDefinedField4", headerName: "User Defined Field 4", width: 250, hide: true },
    { field: "userDefinedField5", headerName: "User Defined Field 5", width: 250, hide: true },

    { field: "lastModifiedBy", headerName: "Last Modified By", width: 250, hide: true },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 250, type: "dateTime", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
  ];

  // Datagrid click event
  const handleDataGridClick = (params: GridRowParams, event: any) => {
    //getFilterData(filterRowType, enrollmentBatchFileItems);
  };

  // Handle enroll all checkbox clicked
  const handleEnrollAllChange = (trueFalse: boolean) => {
    setEnrollAll(trueFalse);

    const updateRows = Array.from(enrollmentBatchFileItems);

    setFilterEnrollmentBatchFileItems([]);

    filterEnrollmentBatchFileItems.forEach(function (filterEnrollmentBatchFileItem) {
      const filterRows = updateRows.filter((item) => item.id === filterEnrollmentBatchFileItem.id);

      if (filterRows.length === 1) {
        if (filterRows[0].allowEdit === true) {
          filterRows[0].readyToEnroll = trueFalse;
          filterRows[0].rowChanged = true;
          setDataChanged(true);
        }
      }
    });

    setTimeout(() => {
      setEnrollmentBatchFileItems(updateRows);
      setFilterEnrollmentBatchFileItems(getFilterData(filterRowType, updateRows));
    }, 500);
  };

  // Update enrollment batch file item attributes
  const updateEnrollmentBatchFileItemAttributes = ({ id, field, value }) => {
    if (enrollmentBatchFileItems.length > 0) {
      const updateRows = Array.from(enrollmentBatchFileItems);
      const filterRows = updateRows.filter((x) => Number(x.id) === Number(id));

      if (filterRows !== undefined) {
        if (filterRows.length === 1) {
          if (filterRows[0].allowEdit === true) {
            if (field === "readyToEnroll") {
              const filterRow = enrollmentBatchFileItems.filter((x) => x.id === Number(id));
              if (filterRow.length > 0) {
                if (filterRow[0].readyToEnroll !== value) {
                  filterRows[0].readyToEnroll = value;
                  filterRows[0].rowChanged = true;
                  setDataChanged(true);
                }
              }
            }
          } else {
            showAlert({ severity: "info", message: "This account can't enroll due to one or more validation fails." });
          }
        }

        setTimeout(() => {
          setEnrollmentBatchFileItems(updateRows);
          setFilterEnrollmentBatchFileItems(getFilterData(filterRowType, updateRows));
        }, 500);
      }
    }
  };

  const handleEditRowsModelChange = React.useCallback(
    (model) => {
      const updatedModel = { ...model };
      Object.keys(updatedModel).forEach((id) => {
        const filterRow = enrollmentBatchFileItems.filter((x) => x.id === Number(id));
        if (filterRow.length > 0) {
          if (filterRow[0].readyToEnroll !== updatedModel[id].readyToEnroll.value) {
            setDataChanged(true);
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterEnrollmentBatchFileItems]
  );

  const handleCellEditStart = (params: GridCellParams, event: any) => {
    const updateRows = Array.from(enrollmentBatchFileItems);
    const filterRows = updateRows.filter((x) => Number(x.id) === Number(params.id));
    if (filterRows.length > 0) {
      if (filterRows[0].allowEdit === false) {
        params.isEditable = false;
        showAlert({ severity: "error", message: "This account cannot be enrolled due to one or more validation errors." });
      }
      else {
        setIsDatagridInEditMode(true);
      }
    }
  };

  // Handle cell edit commit
  const handleCellEditCommit = React.useCallback(
    ({ id, field, value }) => {
      setTimeout(() => {
        updateEnrollmentBatchFileItemAttributes({ id, field, value });
        setIsDatagridInEditMode(false);
      }, 25);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterEnrollmentBatchFileItems]
  );

  // More menu click event
  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  // Close more menu
  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  // Get confirmation to enroll
  const getEnrollConfirmation = () => {
    if (dataChanged) {
      return;
    }

    if (enrollRows <= 0) {
      showAlert({ message: "No data to enroll.", severity: "info" });
      return
    }

    setEnrollConfirmDialogOpen(true);
  };

  // Enroll confirmation status
  const enrollConfirmation = async (confirm: boolean) => {
    setEnrollConfirmDialogOpen(false);

    if (confirm) {
      await promote();
    }
  };

  // Convert base64 string to bytes
  const base64ToBytes = (base64) => {
    var s = window.atob(base64);
    var bytes = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) {
      bytes[i] = s.charCodeAt(i);
    }
    return bytes;
  };

  // Download report
  const downloadReport = async () => {
    const payload = {
      filterType: filterRowType,
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/enrollment/batch/files/" + preBatchLoadKey + "/export", payload)
        .then(async (response) => {
          setSaveInProgress(false);

          if (response.data.content === null) {
            showAlert({ message: "Unable to download report.", severity: "error" });
            return;
          }

          var blob = new Blob([base64ToBytes(response.data.content)]);

          const fileName = "file.xlsx";
          if (navigator.msSaveBlob) {
            // IE 10+
            navigator.msSaveBlob(blob, fileName);
          } else {
            const link = document.createElement("a");
            // Browsers that support HTML5 download attribute
            if (link.download !== undefined) {
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute("download", fileName);
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }

          showAlert({ message: "Downloading report.", severity: "success" });

          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to export enrollment pending file items."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Get delete confirmation
  const getDeleteConfirmation = async () => {
    if (!canWriteEnrollmentFile(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    setDeleteConfirmDialogOpen(true);
  };

  // Delete file
  const deleteConfirmation = async (confirm: boolean) => {
    setDeleteConfirmDialogOpen(false);

    if (confirm) {
      const payload = {};

      try {
        setSaveInProgress(true);
        await API.delete("/api/enrollment/batch/files/" + preBatchLoadKey, payload)
          .then(async (response) => {
            showAlert({ message: "Successfully deleted.", severity: "success" });
            await refreshData("enrollmentBatches");
            setSaveInProgress(false);
            goToRoutePage("enrollment/files");
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            showAlert({
              message: apiReponseError(error, "Failed to delete enrollment pending file."),
              severity: "error",
            });
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Promote enrollment batch file all item attributes
  const promote = async () => {
    if (!canWriteEnrollmentFile(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    const updatedRows: any[] = [];
    enrollmentBatchFileItems.forEach(function (enrollmentBatchFileItem) {
      if (enrollmentBatchFileItem.isRecordValid) {
        updatedRows.push({
          preBatchLoadItemKey: enrollmentBatchFileItem.preBatchLoadItemKey,
          readyToEnroll: enrollmentBatchFileItem.readyToEnroll,
        });
      }
    });

    if (dataChanged) {
      showAlert({ message: "Press 'Update' button before enroll accounts", severity: "success" });
      return;
    }

    if (updatedRows.length === 0) {
      return;
    }

    const payload = {};

    try {
      setSaveInProgress(true);
      await API.post("/api/enrollment/batch/files/" + preBatchLoadKey + "/promote", payload)
        .then(async (response) => {
          setSaveInProgress(false);
          showAlert({ message: "Successfully saved.", severity: "success" });

          try {
            const tempPreBatchLoadKey = response.data.preBatchLoadKey;
            if (tempPreBatchLoadKey !== null && tempPreBatchLoadKey !== "" && tempPreBatchLoadKey !== undefined) {
              refresh(true);
              return;
            }
          }
          catch { }

          await refreshData("enrollmentBatches");
          goToRoutePage("enrollment/files");
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to promote enrollment pending file items."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Save enrollment batch file all item attributes
  const save = async () => {
    if (!canWriteEnrollmentFile(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    const updatedRows: any[] = [];
    enrollmentBatchFileItems.forEach(function (enrollmentBatchFileItem) {
      if (enrollmentBatchFileItem.rowChanged) {
        updatedRows.push({
          preBatchLoadItemKey: enrollmentBatchFileItem.preBatchLoadItemKey,
          readyToEnroll: enrollmentBatchFileItem.readyToEnroll,
        });
      }
    });

    if (updatedRows.length === 0) {
      return;
    }

    const payload = {
      preEnrollBatchPendingFileItems: updatedRows,
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/enrollment/batch/files/" + preBatchLoadKey, payload)
        .then(async (response) => {
          setSaveInProgress(false);
          showAlert({ message: "Successfully saved.", severity: "success" });
          refresh(true);
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save enrollment pending file items."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Set filter data
  const setFilterData2 = (rowType: number, items: PreEnrollBatchPendingFileItem[]) => {
    setFilterRowType(rowType);
    setFilterEnrollmentBatchFileItems(getFilterData(rowType, items));

    if (rowType === 1) {
      setEnrollAll(true);
    } else if (rowType === -1) {
      setEnrollAll(false);
    }
  };

  // Set filter data
  const setFilterData = (rowType: number) => {
    setFilterData2(rowType, enrollmentBatchFileItems);
  };

  // Get filter data
  const getFilterData = (rowType: number, items: PreEnrollBatchPendingFileItem[]) => {
    let rows: PreEnrollBatchPendingFileItem[] = [];

    if (rowType === 1) {
      rows = items.filter((x: any) => x.readyToEnroll === true);
      setEnrollRows(rows.length);
      setIssueRows(items.length - rows.length);
    } else if (rowType === -1) {
      rows = items.filter((x: any) => x.readyToEnroll === false);
      setIssueRows(rows.length);
      setEnrollRows(items.length - rows.length);
    } else {
      rows = items;
    }
    return rows;
  };

  // Refresh data
  const refresh = async (startUp: boolean) => {
    try {
      setLoading(true);
      setDataChanged(false);
      setEnrollmentBatchFileItems([]);

      const apiResponse = await getPreEnrollBatchPendingFileItems(preBatchLoadKey);

      if (apiResponse.success) {
        setEnrollmentBatchFileItems(apiResponse.data.preEnrollBatchPendingFileItems);
        setFilterEnrollmentBatchFileItems(getFilterData(filterRowType, apiResponse.data.preEnrollBatchPendingFileItems));

        apiResponse.data.preEnrollBatchPendingFileItems.forEach(function (preEnrollBatchPendingFileItem) {
          preEnrollBatchPendingFileItem.rowChanged = false;
        });

        if (startUp) {
          if (apiResponse.data.preEnrollBatchPendingFileItems.filter((x) => x.readyToEnroll === false).length > 0) {
            setFilterData2(-1, apiResponse.data.preEnrollBatchPendingFileItems);
          } else {
            setFilterData2(1, apiResponse.data.preEnrollBatchPendingFileItems);
          }
        }

        setLoading(false);
      } else {
        setLoading(false);
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  useEffect(() => {
    writeLog("Enrollment Files Review", "", "", "");
    setWindowTitle("Enrollment Files Review");
    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        setLoading(false);
      }

      if (!canReadEnrollmentFile(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      if (preBatchLoadKey === undefined || preBatchLoadKey === null || preBatchLoadKey === "") {
        goToRoutePage("enrollment/files");
        return;
      }

      enrollmentBatches.forEach(function (enrollmentBatch) {
        const filterRows = enrollmentBatch.enrollmentBatchFiles.filter((item) => item.preBatchLoadKey === preBatchLoadKey);

        if (filterRows.length === 1) {
          setListTitle(filterRows[0].clientCode + " " + filterRows[0].clientName);
          setFileId(filterRows[0].fileId);
        }
      });
    };

    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, preBatchLoadKey, userFunctionAccess]);

  useEffect(() => {
    refresh(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <SaveBackdrop open={saveInProgress} />

          <Confirm title="Confirmation" content={`<h4>Do you want to enroll the selected <u>${enrollRows}</u> records. </h3><p><span style='color:red'><b>TIP:</b></span> <u>Download the report before clicking <b>'Yes'</b> to keep track of your enrollments.</u></p>`} yesText="YES" noText="NO" open={enrollConfirmDialogOpen} callbackOnClose={() => { }} callbackOnConfirm={enrollConfirmation} />

          <Confirm title="Confirmation" content={`<p>Are you sure you want to remove this file?</p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpen} callbackOnClose={() => { }} callbackOnConfirm={deleteConfirmation} />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs style={{ paddingTop: "10px" }} maxItems={getBreadcrumbMaxItems(3)}>
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Link color="inherit" onClick={() => goToRoutePage("enrollment/files")} className={globalStyles.link}>
                      Enrollment Files
                    </Link>
                    {fileId > 0 ? <Typography color="textPrimary">{fileId}</Typography> : <></>}
                    <Typography color="textPrimary">{listTitle}</Typography>
                    <Typography color="textPrimary">Review</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Hidden xsDown>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    {canWriteEnrollmentFile(userFunctionAccess) && !dataChanged && enrollRows > 0 ? (
                      <IconButton>
                        <Tooltip arrow title={"Enroll selected accounts."}>
                          <EnrollIcon color="secondary" onClick={() => getEnrollConfirmation()} />
                        </Tooltip>
                      </IconButton>
                    ) : null}

                    {canWriteEnrollmentFile(userFunctionAccess) && !dataChanged ? (
                      <IconButton>
                        <Tooltip arrow title={"Delete this file."}>
                          <DeleteIcon color="error" onClick={() => getDeleteConfirmation()} />
                        </Tooltip>
                      </IconButton>
                    ) : null}

                    {!dataChanged ? (
                      <IconButton>
                        <Tooltip arrow title={"Download Status Report"}>
                          <GetAppToneIcon color="secondary" onClick={() => downloadReport()} />
                        </Tooltip>
                      </IconButton>
                    ) : null}

                    <IconButton>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" onClick={() => refresh(false)} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Hidden>

                <Hidden smUp>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <Tooltip arrow title="Menu">
                      <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                        <MoreIcon />
                      </Button>
                    </Tooltip>
                    <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>
                      {canWriteEnrollmentFile(userFunctionAccess) && !dataChanged && enrollRows > 0 ? (
                        <StyledMenuItem
                          onClick={(e) => {
                            getEnrollConfirmation();
                            handleGridMoreMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <EnrollIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={"Enroll selected accounts"} />
                        </StyledMenuItem>
                      ) : null}

                      {canWriteEnrollmentFile(userFunctionAccess) ? (
                        <StyledMenuItem
                          onClick={(e) => {
                            getDeleteConfirmation();
                            handleGridMoreMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <EnrollIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={"Delete this file"} />
                        </StyledMenuItem>
                      ) : null}

                      {!dataChanged ? (
                        <StyledMenuItem
                          onClick={(e) => {
                            downloadReport();
                            handleGridMoreMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <GetAppToneIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={"Download Status Report"} />
                        </StyledMenuItem>
                      ) : null}
                      <StyledMenuItem
                        onClick={(e) => {
                          refresh(false);
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <RefreshIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Refresh "} />
                      </StyledMenuItem>
                    </Menu>
                  </Box>
                </Hidden>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={5} md={7}>
                <Box p={0} display={"flex"} justifyContent={"left"} alignContent={"left"}>
                  <FormGroup row className={pageStyles.actionCheckbox}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enrollAll}
                          onChange={(event: any) => {
                            handleEnrollAllChange(event.target.checked);
                          }}
                        />
                      }
                      label="Set all to enroll"
                    />
                  </FormGroup>
                </Box>
              </Grid>
              <Grid item xs={12} sm={7} md={5}>
                <Box p={0} className={pageStyles.actionButtonGroup}>
                  <ButtonGroup color="default" size="small" variant="outlined">
                    <Button variant={filterRowType === 0 ? "contained" : "outlined"} color={filterRowType === 0 ? "secondary" : "default"} onClick={() => setFilterData(0)} className={pageStyles.actionButton}>
                      Show All
                    </Button>
                    <Button variant={filterRowType === 1 ? "contained" : "outlined"} color={filterRowType === 1 ? "secondary" : "default"} onClick={() => setFilterData(1)} className={pageStyles.actionButton}>
                      <Badge badgeContent={enrollRows} color="primary" showZero>
                        Enroll&nbsp;&nbsp;&nbsp;
                      </Badge>
                    </Button>
                    <Button variant={filterRowType === -1 ? "contained" : "outlined"} color={filterRowType === -1 ? "secondary" : "default"} onClick={() => setFilterData(-1)} className={pageStyles.actionButton}>
                      <Badge badgeContent={issueRows} color="primary" showZero>
                        Issue&nbsp;&nbsp;&nbsp;&nbsp;
                      </Badge>
                    </Button>
                  </ButtonGroup>
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={3} className={globalStyles.formikContainer}>
              <Grid item xs={12}>
                <Box className={pageStyles.datagridContainer}>
                  <Box p={2} display={"flex"} justifyContent={"center"} alignContent={"center"}>
                    <small>Double click on the 'Enroll' data cell to edit.</small>
                  </Box>
                  <CustomDataGrid uniqueGridCode={"EnrollmentFileItemsResult"} columns={columns} rows={filterEnrollmentBatchFileItems} showToolBar={true} sortModel={[{ field: "lastModifiedDateTime", sort: "desc" }]} handleDataGridClick={handleDataGridClick} handleCellEditCommit={handleCellEditCommit} handleCellEditStart={handleCellEditStart} handleEditRowsModelChange={handleEditRowsModelChange} />
                </Box>
              </Grid>
            </Grid>

            {canWriteEnrollmentFile(userFunctionAccess) && dataChanged && !isDatagridInEditMode ? (

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box p={1} ></Box>
                  <Box p={3} display={"flex"} justifyContent={"center"} alignContent={"center"}>

                    <Button
                      id="button-save"
                      variant="contained"
                      color="secondary"
                      className={globalStyles.buttonDefault}
                      onClick={() => {
                        save();
                      }}
                    >
                      Update
                    </Button>

                    <Button
                      id="button-cancel"
                      onClick={() => {
                        refresh(true);
                      }}
                      className={globalStyles.buttonDefault}
                    >
                      <u>Cancel</u>
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <></>
            )}
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      minHeight: "350px",
      height: "calc(100vh - 400px)",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    actionButtonGroup: {
      justifyContent: "right",
      alignContent: "center",
      display: "flex",

      [theme.breakpoints.up("sm")]: {
        paddingTop: "15px",
      },

      [theme.breakpoints.down("xs")]: {
        justifyContent: "left",
        alignContent: "left",
        paddingBottom: "10px",
      },
    },
    actionButton: {
      fontSize: "0.8em",
    },
    actionCheckbox: {
      "& .MuiTypography-body1": {
        fontSize: "0.8em",
        fontWeight: 500,
        textTransform: "uppercase !important",
      },
    },
  })
);

export default EnrollmentFileReview;
